import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Pagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RuleIcon from '@mui/icons-material/Rule';
import PublicIcon from '@mui/icons-material/Public';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getRules, Rule, processApproval, deleteRule, createRule, validateRuleName } from '../graphql';
import { uploadExcelFile, downloadTemplate, UrlGroup } from '../api/excelUploadService';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { COUNTRY_OPTIONS } from '../data/countries';
import SuccessNotification from './SuccessNotification';
import ErrorNotification from './ErrorNotification';
import ConfirmationDialog from './ConfirmationDialog';
import RejectionDialog from './RejectionDialog';
import TestRuleDialog from './TestRuleDialog';
import DocsPage from './DocsPage';
import FAQsPage from './FAQsPage';
import ReleaseNotes from './ReleaseNotes';
import { 
  calculateRuleStatus, 
  getStatusColor,
  formatToLocalTime,
  combineDateTimeToUTC
} from '../utils/dateTimeHelper';

interface MainContentProps {
  currentView: string;
  onViewChange?: (view: string) => void;
  isAdmin?: boolean;
}

const MainContent: React.FC<MainContentProps> = ({ currentView, onViewChange, isAdmin = false }) => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['Active']);
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState<string[]>(['Active']);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 9; // Fixed at 9 rows per page
  const [totalCount, setTotalCount] = useState(0); // Total count from backend
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // 'desc' = newest first
  const [sortBy, setSortBy] = useState<'createdAt' | null>('createdAt'); // Default sort by creation date
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successNotifMessage, setSuccessNotifMessage] = useState('');
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorNotifMessage, setErrorNotifMessage] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [ruleToApprove, setRuleToApprove] = useState<Rule | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [ruleToReject, setRuleToReject] = useState<Rule | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<Rule | null>(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [ruleToTest, setRuleToTest] = useState<Rule | null>(null);
  const ADOBE_ANALYTICS_URL = 'https://experience.adobe.com/#/@nike/so:nike1/analytics/spa/index.html#/workspace/edit/68003626ce04b8fe413edf51?projectId=68003626ce04b8fe413edf51';

  const handleOpenAdobeAnalytics = () => {
    window.open(ADOBE_ANALYTICS_URL, '_blank', 'noopener,noreferrer');
  };

  // Audit log state
  const [auditRules, setAuditRules] = useState<Rule[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(0);
  const auditRowsPerPage = 9;
  const [auditSearchQuery, setAuditSearchQuery] = useState('');
  const [isAuditBackendSearch, setIsAuditBackendSearch] = useState(false);
  
  // Rules page filters
  const [rulesSearchQuery, setRulesSearchQuery] = useState('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  
  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState<UrlGroup[]>([]);
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importFileName, setImportFileName] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  const [selectedImportRows, setSelectedImportRows] = useState<Set<number>>(new Set());
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkCreateResults, setBulkCreateResults] = useState<{ index: number; ruleName: string; success: boolean; message: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debounce timer for search
  const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  const statusOptions = ['Submitted', 'Active', 'Inactive', 'Scheduled'];

  // Reset filters and page when view changes (My Rules → Approvals → Audit Log)
  useEffect(() => {
    setPage(0);
    setAuditPage(0);
    setSelectedStatuses(['Active']);
    setTempSelectedStatuses(['Active']);
    setRulesSearchQuery('');
    setStartDateFilter('');
    setEndDateFilter('');
  }, [currentView]);

  // Helper function to convert email to formatted name
  const formatCreatorName = (email: string) => {
    if (!email || email === 'Unknown' || email === 'unknown@nike.com') return 'Unknown';
    
    // Extract username before @
    const username = email.split('@')[0];
    
    // Split by dot and capitalize each part
    const nameParts = username.split('.');
    const formattedName = nameParts
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
      
    return formattedName;
  };

  // Helper function to get actual rule status based on current time in user's timezone
  const getActualRuleStatus = (rule: Rule): string => {
    // Calculate status based on user's local time
    const calculatedStatus = calculateRuleStatus({
      ruleStartFrom: rule.ruleStartFrom,
      ruleEndAt: rule.ruleEndAt
    });
    
    // Return capitalized version
    return calculatedStatus.charAt(0).toUpperCase() + calculatedStatus.slice(1);
  };

  const handleApply = () => {
    setSelectedStatuses(tempSelectedStatuses);
    setDropdownOpen(false);
    setDropdownAnchor(null);
    setPage(0); // Reset to first page when filters change
  };

  const handleClear = () => {
    setTempSelectedStatuses([]);
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTempSelectedStatuses(selectedStatuses);
    setDropdownAnchor(event.currentTarget);
    setDropdownOpen(true);
  };

  const handleDropdownClose = () => {
    setTempSelectedStatuses(selectedStatuses);
    setDropdownOpen(false);
    setDropdownAnchor(null);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage - 1); // Pagination component uses 1-based indexing
    // Trigger refetch with new page
    fetchRules(rulesSearchQuery, startDateFilter, endDateFilter, newPage - 1);
  };

  const handleSortByCreated = () => {
    if (sortBy === 'createdAt') {
      // Toggle between asc and desc
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      // First click: set to createdAt and desc (newest first)
      setSortBy('createdAt');
      setSortOrder('desc');
    }
  };

  const handleExportRules = () => {
    try {
      // Custom headers for marketing team
      const headers = [
        'Rule Name',
        'Redirect URL',
        'Status',
        'Product Name',
        'Style Colour Codes',
        'Countries',
        'Divisions',
        'Start Date & Time',
        'End Date & Time',
        'Created By',
        'Created On'
      ];

      const csvRows = [headers.join(',')];

      // Helper function: Format date for better readability in Excel
      const formatDateForExcel = (isoDate: string) => {
        if (!isoDate) return '';
        try {
          const d = new Date(isoDate);
          
          // Format: DD/MM/YYYY HH:MM AM/PM (in user's local timezone)
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = d.getFullYear();
          
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, '0');
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          
          return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
        } catch {
          return '';
        }
      };

      // Helper function: Format arrays
      const formatArray = (arr: string[] | undefined) => {
        if (!arr || arr.length === 0) return '';
        return arr.join('; ');
      };

      // Helper function: Format name from email
      const formatName = (email: string) => {
        if (!email) return '';
        const name = email.split('@')[0];
        return name.split('.').map(part => 
          part.charAt(0).toUpperCase() + part.slice(1)
        ).join(' ');
      };

      // Helper function: Convert country codes to full names
      const formatCountries = (countryCodes: string[] | undefined) => {
        if (!countryCodes || countryCodes.length === 0) return '';
        return countryCodes
          .map(code => {
            const country = COUNTRY_OPTIONS.find(c => c.code === code);
            return country ? country.name : code;
          })
          .join('; ');
      };

      sortedRules.forEach(rule => {
        // Calculate actual status based on user's local time
        const actualStatus = getActualRuleStatus(rule);
        
        const row = [
          `"${(rule.name || '').replace(/"/g, '""')}"`,
          `"${(rule.redirectUrl || '').replace(/"/g, '""')}"`,
          actualStatus || '',
          `"${(rule.athleteFullName || '').replace(/"/g, '""')}"`,
          `"${formatArray(rule.styleColorCodes).replace(/"/g, '""')}"`,
          `"${formatCountries(rule.countries).replace(/"/g, '""')}"`,
          `"${formatArray(rule.divisions).replace(/"/g, '""')}"`,
          `"${formatDateForExcel(rule.ruleStartFrom || '')}"`,
          `"${formatDateForExcel(rule.ruleEndAt || '')}"`,
          `"${formatName(rule.createdBy || '')}"`,
          `"${formatDateForExcel(rule.createdAt)}"`
        ];
        csvRows.push(row.join(','));
      });

      // Add BOM for proper UTF-8 encoding in Excel
      const csvContent = '\uFEFF' + csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Clean filename with today's date
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      link.download = `QR-Redirect-Rules-${dateStr}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting rules:', error);
      console.error('Failed to export rules');
    }
  };

  const handleImportClick = () => {
    setImportData([]);
    setImportWarnings([]);
    setImportError(null);
    setImportFileName('');
    setImportLoading(false);
    setDragActive(false);
    setSelectedImportRows(new Set());
    setBulkCreateResults([]);
    setBulkCreating(false);
    setImportDialogOpen(true);
  };

  const processFile = async (file: File) => {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setImportError('Only .xlsx and .xls files are allowed');
      return;
    }
    setImportFileName(file.name);
    setImportLoading(true);
    setImportError(null);
    setImportData([]);
    setImportWarnings([]);

    try {
      const result = await uploadExcelFile(file);
      setImportData(result.data);
      setImportWarnings(result.warnings);
    } catch (err: any) {
      setImportError(err.message || 'Failed to parse Excel file');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
    } catch (err: any) {
      console.error('Failed to download template:', err);
    }
  };

  const isValidStyleColorCode = (code: string): boolean => /^[A-Z\d]{5,}-\d{3}$/i.test(code);
  const isValidStyleCode = (code: string): boolean => /^[A-Z\d]{5,}$/i.test(code);

  const allStyleColorCodesValid = (item: UrlGroup): boolean =>
    item.styleColorCodes.length > 0 && item.styleColorCodes.every(isValidStyleColorCode);

  const allStyleCodesValid = (item: UrlGroup): boolean =>
    item.styleCodes.length === 0 || item.styleCodes.every(isValidStyleCode);

  const isRowValid = (item: UrlGroup): boolean =>
    item.httpsValid &&
    (item.styleColorCodes.length === 0 || item.styleColorCodes.every(isValidStyleColorCode)) &&
    (item.styleCodes.length === 0 || item.styleCodes.every(isValidStyleCode));

  const getRowErrors = (item: UrlGroup, index: number): string[] => {
    const errors: string[] = [];
    if (!item.httpsValid) {
      errors.push(`Row ${index + 1}: URL "${item.url}" is not using HTTPS`);
    }
    const badStyleColors = item.styleColorCodes.filter(c => !isValidStyleColorCode(c));
    if (badStyleColors.length > 0) {
      errors.push(`Row ${index + 1}: Invalid Style Color Code(s): ${badStyleColors.join(', ')}`);
    }
    const badStyles = item.styleCodes.filter(c => !isValidStyleCode(c));
    if (badStyles.length > 0) {
      errors.push(`Row ${index + 1}: Invalid Style Code(s): ${badStyles.join(', ')}`);
    }
    return errors;
  };

  const generateRuleName = (item: UrlGroup, index: number): string => {
    try {
      if (item.ruleName) {
        return item.ruleName;
      }

      if (item.sectionName) {
        return `${item.sectionName} - ${index + 1}`;
      }

      const url = new URL(item.url);
      const pathParts = url.pathname.split('/').filter(Boolean);
      const meaningfulParts = pathParts.filter(p => !['w', 't', 'u', 'p'].includes(p) && p.length > 1);
      const eventName = (meaningfulParts.length > 0 ? meaningfulParts.slice(-2) : ['Redirect'])
        .map(part => part.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
        .join(' - ');

      return `${eventName} - ${index + 1}`;
    } catch {
      return `Redirect Rule ${index + 1}`;
    }
  };

  const handleToggleImportRow = (index: number) => {
    if (!isRowValid(importData[index])) return;
    setSelectedImportRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const validRowIndices = importData.map((item, i) => ({ i, valid: isRowValid(item) })).filter(r => r.valid).map(r => r.i);

  const handleToggleAllImportRows = () => {
    const allValidSelected = validRowIndices.length > 0 && validRowIndices.every(i => selectedImportRows.has(i));
    if (allValidSelected) {
      setSelectedImportRows(new Set());
    } else {
      setSelectedImportRows(new Set(validRowIndices));
    }
  };

  const resolveUniqueRuleName = async (baseName: string): Promise<string> => {
    const { isValid } = await validateRuleName(baseName);
    if (isValid) return baseName;
    for (let suffix = 1; suffix <= 100; suffix++) {
      const candidate = `${baseName}-${suffix}`;
      const check = await validateRuleName(candidate);
      if (check.isValid) return candidate;
    }
    return `${baseName}-${Date.now()}`;
  };

  const handleBulkCreateRules = async () => {
    if (selectedImportRows.size === 0) return;
    setBulkCreating(true);
    setBulkCreateResults([]);
    const results: { index: number; ruleName: string; success: boolean; message: string }[] = [];

    const priorityMap: Record<string, number> = { low: 25, medium: 50, high: 75 };

    for (const index of Array.from(selectedImportRows).sort((a, b) => a - b)) {
      const item = importData[index];
      const baseName = generateRuleName(item, index);
      let ruleName: string;
      try {
        ruleName = await resolveUniqueRuleName(baseName);
      } catch {
        ruleName = baseName;
      }
      const priorityNum = priorityMap[item.priority?.toLowerCase()] ?? 50;

      const ruleStartFrom = combineDateTimeToUTC(
        item.startDate || undefined,
        item.startTime || '00:00'
      );

      let ruleEndAt: string | undefined;
      if (!item.noExpiration && item.expirationDate) {
        ruleEndAt = combineDateTimeToUTC(
          item.expirationDate,
          item.expirationTime || '00:00'
        );
      }

      try {
        await createRule({
          name: ruleName,
          priority: priorityNum,
          redirectUrl: item.url,
          ruleStartFrom,
          ruleEndAt,
          styleColorCodes: item.styleColorCodes.length > 0 ? item.styleColorCodes : undefined,
          styleCodes: item.styleCodes.length > 0 ? item.styleCodes : undefined,
          gtins: item.gtins?.length > 0 ? item.gtins : undefined,
          qrs: item.qrs?.length > 0 ? item.qrs : undefined,
          countries: item.country ? [item.country] : undefined,
          categories: item.category ? [item.category] : undefined,
          divisions: item.division ? [item.division] : undefined,
          brandName: item.brandName || undefined,
          athleteFullName: item.athleteFullName || undefined,
          boundingBox: item.boundingBox || undefined,
        });
        results.push({ index, ruleName, success: true, message: 'Created successfully' });
      } catch (err: any) {
        results.push({ index, ruleName, success: false, message: err.message || 'Failed to create rule' });
      }
      setBulkCreateResults([...results]);
    }

    setBulkCreating(false);
    fetchRules();
  };


  const handleViewClick = (rule: Rule) => {
    setSelectedRule(rule);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedRule(null);
  };

  const handleApprove = async (rule: Rule) => {

    if (!rule.ruleId) return;
    
    // Show custom confirmation dialog

    setRuleToApprove(rule);
    setShowConfirmDialog(true);

  };

  const sendToClaravineExtension = (ruleId: string, link: string, campaignNameDetails: string): boolean => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('authToken') || '';
      const el = document.createElement('div');
      el.id = 'claravine-process-rule';
      el.setAttribute('data-rule-id', ruleId);
      el.setAttribute('data-link', link);
      el.setAttribute('data-campaign-name', campaignNameDetails);
      el.setAttribute('data-auth-token', token);
      el.style.display = 'none';
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 5000);
      return true;
    } catch {
      return false;
    }
  };

  const handleConfirmApprove = async () => {

    if (!ruleToApprove?.ruleId) return;

    setShowConfirmDialog(false);
    
    try {
      const result = await processApproval(ruleToApprove.ruleId, 'approve');
      if (result.success) {
        const claravineTriggered = sendToClaravineExtension(
          ruleToApprove.ruleId,
          ruleToApprove.redirectUrl,
          ruleToApprove.name
        );

        const message = claravineTriggered
          ? 'Rule approved! Tracking URL generating (~2 min)...'
          : (result.message || 'Rule approved successfully!');
        setSuccessNotifMessage(message);
        setShowSuccessNotification(true);
      } else {
        setErrorNotifMessage(result.message || 'Failed to approve rule');
        setShowErrorNotification(true);
      }
    } catch (error: any) {
      setErrorNotifMessage(`Error: ${error.message}`);
      setShowErrorNotification(true);
    }
    
    setRuleToApprove(null);
  };

  const handleReject = async (rule: Rule) => {
    if (!rule.ruleId) return;
    
    // Show custom rejection dialog
    setRuleToReject(rule);
    setShowRejectDialog(true);
  };

  const handleConfirmReject = async (reason: string) => {
    if (!ruleToReject?.ruleId) return;
    
    setShowRejectDialog(false);
    
    try {
      const result = await processApproval(ruleToReject.ruleId, 'reject', reason);
      if (result.success) {
        setSuccessNotifMessage(result.message || 'Rule rejected successfully!');
        setShowSuccessNotification(true);
      } else {
        setErrorNotifMessage(result.message || 'Failed to reject rule');
        setShowErrorNotification(true);
      }
    } catch (error: any) {
      setErrorNotifMessage(`Error: ${error.message}`);
      setShowErrorNotification(true);
    }
    
    setRuleToReject(null);
  };

  const handleDelete = async (rule: Rule) => {
    if (!rule.ruleId) return;
    
    // Show custom delete confirmation dialog
    setRuleToDelete(rule);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!ruleToDelete?.ruleId) return;
    
    setShowDeleteDialog(false);
    
    try {
      const result = await deleteRule(ruleToDelete.ruleId);
      if (result.success) {
        setSuccessNotifMessage(result.message || 'Delete request submitted successfully!');
        setShowSuccessNotification(true);
      } else {
        setErrorNotifMessage(result.message || 'Failed to delete rule');
        setShowErrorNotification(true);
      }
    } catch (error: any) {
      setErrorNotifMessage(`Error: ${error.message}`);
      setShowErrorNotification(true);
    }
    
    setRuleToDelete(null);
  };

  const handleEditClick = (rule: Rule) => {
    // Always navigate to edit - pending changes will be detected on update
    navigateToEdit(rule);
  };

  const navigateToEdit = (rule: Rule) => {
    navigate('/create-rule', {
      state: {
        mode: 'edit',
        ruleData: rule
      }
    });
  };

  // Group audit entries by rule for timeline card view
  const groupedAuditRules = useMemo(() => {

    if (auditRules.length > 0) {

    }
    const groups: any[] = [];
    
    auditRules.forEach(rule => {

      // Always show rules - create CREATED event if no changeHistory exists
      let allEvents = rule.changeHistory || [];
      
      // If no changeHistory, create an INSERT event from rule creation data
      if (!allEvents || allEvents.length === 0) {
        allEvents = [{
          status: 'INSERT',
          timestamp: new Date(rule.createdAt).getTime() / 1000, // Convert to seconds
          operationType: 'INSERT',
          requestedBy: rule.createdBy,
          modifiedBy: rule.createdBy,
          changes: null
        }];

      }
      
      if (allEvents.length > 0) {
        // Sort events newest to oldest for timeline display (latest changes at top)
        const sortedEvents = [...allEvents].sort((a, b) => b.timestamp - a.timestamp);
        
        groups.push({
          ruleId: rule.ruleId,
          ruleName: rule.name,
          events: sortedEvents,
          totalEvents: allEvents.length
        });
      }
    });

    // Filter by search query
    const filteredGroups = auditSearchQuery.trim() 
      ? groups.filter(group => 
          group.ruleName.toLowerCase().includes(auditSearchQuery.toLowerCase())
        )
      : groups;

    // Return filtered groups without re-sorting
    // Rules are already sorted by updatedAt DESC from backend/frontend
    return filteredGroups;
  }, [auditRules, auditSearchQuery]);

  const paginatedAuditRules = useMemo(() => {
    return groupedAuditRules.slice(
      auditPage * auditRowsPerPage, 
      auditPage * auditRowsPerPage + auditRowsPerPage
    );
  }, [groupedAuditRules, auditPage, auditRowsPerPage]);
  
  const totalAuditPages = Math.ceil(groupedAuditRules.length / auditRowsPerPage);

  // Sort rules based on sortBy and sortOrder
  // Note: Backend handles filtering and pagination, frontend only does client-side sorting
  const sortedRules = useMemo(() => {
    // No client-side filtering needed - backend does it all
    let filteredRules = rules;
    
    // Special sorting for Approvals page - sort by approvedAt (newest first)
    if (currentView === 'approvals') {
      return [...filteredRules].sort((a, b) => {
        // For pending items, use createdAt; for approved items, use approvedAt
        const dateA = a.approvedAt 
          ? new Date(a.approvedAt).getTime() 
          : new Date(a.createdAt).getTime();
        const dateB = b.approvedAt 
          ? new Date(b.approvedAt).getTime() 
          : new Date(b.createdAt).getTime();
        return dateB - dateA; // Newest first (most recently approved at top)
      });
    }
    
    // For other views (my-rules), use existing sortBy logic
    if (!sortBy) return filteredRules;
    
    const sorted = [...filteredRules].sort((a, b) => {
      if (sortBy === 'createdAt') {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        
        if (sortOrder === 'desc') {
          return dateB - dateA; // Newest first
        } else {
          return dateA - dateB; // Oldest first
        }
      }
      return 0;
    });
    
    return sorted;
  }, [rules, sortBy, sortOrder, currentView]);

  // Backend pagination - no client-side slicing needed
  const paginatedRules = sortedRules;
  
  // Calculate total pages from backend totalCount (fallback to count if not available)
  const effectiveTotalCount = totalCount || rules.length;
  const totalPages = Math.ceil(effectiveTotalCount / rowsPerPage);

  const fetchRules = useCallback(async (searchQuery?: string, startDate?: string, endDate?: string, pageNum?: number) => {
    setLoading(true);
    setError(null);
    try {
      let response;
      const currentPage = pageNum !== undefined ? pageNum : page;
      
      // Determine if we need client-side filtering (slow path)
      const hasSearchFilter = searchQuery && searchQuery.trim().length >= 3;
      const hasStatusFilter = selectedStatuses.length > 0;
      
      // Apply different filters based on view
      if (currentView === 'my-rules') {

        // Determine which statuses to fetch from backend
        // Backend now supports ruleStatuses array - use single API call for multi-select
        
        let fetchStatuses: string[] = [];
        let onlyFetchPending = false;
        
        if (hasStatusFilter && selectedStatuses.length > 0) {
          const mappedStatuses = selectedStatuses.map(s => s.toLowerCase());
          const hasSubmitted = mappedStatuses.includes('submitted');
          const hasOnlySubmitted = hasSubmitted && mappedStatuses.length === 1;
          
          fetchStatuses = [...mappedStatuses];
          onlyFetchPending = hasOnlySubmitted;
        } else {
          // No status filter - default to active
          fetchStatuses = ['active'];
        }

        // Build filter - use ruleStatuses array for efficient single API call
        const filter: any = {
          limit: rowsPerPage,
          offset: currentPage * rowsPerPage,
          orderBy: 'createdAt',     // Sort by creation date
          sortOrder: 'DESC'         // Newest first
        };
        
        if (hasSearchFilter) {
          filter.ruleStatus = 'all';
          filter.name = searchQuery!.trim();

        } else if (fetchStatuses.length > 1) {
          filter.ruleStatuses = fetchStatuses;

        } else {
          filter.ruleStatus = fetchStatuses[0];

          if (onlyFetchPending) {
            filter.approvalStatus = 'pending';

          }
        }
        
        // Add date filters
        if (startDate) {
          filter.fromDate = startDate;
        }
        
        if (endDate) {
          filter.toDate = endDate;
        }
        
        // Pagination ready - uncomment after testing:
        // filter.limit = rowsPerPage;
        // filter.offset = currentPage * rowsPerPage;
        
        // Single API call for all selected statuses
        try {

          response = await getRules(filter);

          if (response.items && response.items.length > 0) {

          }
        } catch (err) {
          console.error(` Error fetching rules:`, err);
          response = { items: [], success: false, count: 0 };
        }
        
        // Backend already filtered - no client-side filtering needed
        let filteredItems = response.items || [];

        // Use backend pagination if available (checks for totalCount field from actual backend response)
        if (response.totalCount !== undefined) {
          // Backend supports pagination - use totalCount from backend

          setRules(filteredItems);
          setTotalCount(response.totalCount);
        } else {
          // Fallback to client-side pagination (for old backend without totalCount)

          const startIdx = currentPage * rowsPerPage;
          const endIdx = startIdx + rowsPerPage;
          const paginatedItems = filteredItems.slice(startIdx, endIdx);

          setRules(paginatedItems);
          setTotalCount(filteredItems.length);
        }
        
      } else if (currentView === 'approvals') {
        // APPROVALS: Show all rules with pending approval status, sorted by newest first

        let filter: any = { 
          ruleStatus: 'all',
          approvalStatus: 'pending',
          limit: rowsPerPage,
          offset: currentPage * rowsPerPage,
          orderBy: 'createdAt',     // Sort by creation/request date
          sortOrder: 'DESC'         // Newest first
        };
        
        // Add search query to filter if provided
        if (searchQuery && searchQuery.trim().length >= 3) {
          filter.name = searchQuery.trim();
        }
        
        // Add date filters
        if (startDate) {
          filter.fromDate = startDate;
        }
        
        if (endDate) {
          filter.toDate = endDate;
        }
        
        response = await getRules(filter);

        // Use backend pagination if available, otherwise client-side
        if (response.totalCount !== undefined) {
          // Backend supports pagination
          setRules(response.items || []);
          setTotalCount(response.totalCount);
        } else {
          // Fallback to client-side pagination
          const allItems = response.items || [];
          const startIdx = currentPage * rowsPerPage;
          const endIdx = startIdx + rowsPerPage;
          const paginatedItems = allItems.slice(startIdx, endIdx);
          setRules(paginatedItems);
          setTotalCount(allItems.length);
        }
      } else {
        // Default: fetch all
        let filter: any = {
          limit: rowsPerPage,
          offset: currentPage * rowsPerPage
        };
        
        // Add search query to filter if provided
        if (searchQuery && searchQuery.trim().length >= 3) {
          filter.name = searchQuery.trim();
        }
        
        // Add date filters
        if (startDate) {
          filter.fromDate = startDate;
        }
        
        if (endDate) {
          filter.toDate = endDate;
        }
        
        response = await getRules(filter);
        
        // Use backend pagination if available, otherwise client-side
        if (response.totalCount !== undefined) {
          // Backend supports pagination
          setRules(response.items || []);
          setTotalCount(response.totalCount);
        } else {
          // Fallback to client-side pagination
          const allItems = response.items || [];
          const startIdx = currentPage * rowsPerPage;
          const endIdx = startIdx + rowsPerPage;
          const paginatedItems = allItems.slice(startIdx, endIdx);
          setRules(paginatedItems);
          setTotalCount(allItems.length);
        }
      }
      
    } catch (err: any) {
      console.error('Error fetching rules:', err);
      setError(err.message || 'Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  }, [currentView, selectedStatuses, page, rowsPerPage]);

  // Debounced search and filter effect - triggers backend search after user stops typing/selecting
  useEffect(() => {
    if (currentView === 'my-rules' || currentView === 'approvals') {
      // Clear previous timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
      
      // Check if search query is too short
      const hasValidSearch = rulesSearchQuery.trim().length === 0 || rulesSearchQuery.trim().length >= 3;
      
      if (!hasValidSearch) {
        return;
      }
      
      // Debounce search and date filters together
      // If no filters at all, fetch immediately
      if (!rulesSearchQuery.trim() && !startDateFilter && !endDateFilter) {
        fetchRules();
        return;
      }
      
      // Set debounce timer - wait 500ms after user stops typing/selecting
      searchDebounceTimer.current = setTimeout(() => {
        fetchRules(
          rulesSearchQuery || undefined, 
          startDateFilter || undefined, 
          endDateFilter || undefined
        );
      }, 500);
      
      // Cleanup timer on unmount
      return () => {
        if (searchDebounceTimer.current) {
          clearTimeout(searchDebounceTimer.current);
        }
      };
    }
  }, [currentView, rulesSearchQuery, startDateFilter, endDateFilter, fetchRules]);

  // Separate effect for status and view changes
  useEffect(() => {
    if (currentView === 'my-rules' || currentView === 'approvals') {
      fetchRules(
        rulesSearchQuery || undefined,
        startDateFilter || undefined,
        endDateFilter || undefined
      );
    } else if (currentView === 'audit-log') {
      // Fetch rules for audit log with pagination to avoid "Transformation too large" error
      // Audit log needs changeHistory which makes payload large
      const fetchAuditData = async () => {
        setAuditLoading(true);
        try {
          // Fetch with pagination - get enough rules to show audit history
          // Sort by updatedAt to show latest activity first
          const response = await getRules({ 
            ruleStatus: 'all',
            limit: 100,  // Increased to show more recent activity
            offset: 0,
            orderBy: 'updatedAt',  // Sort by latest activity (created/approved/updated)
            sortOrder: 'DESC'       // Newest first
          });
          
          // Frontend sorting to ensure latest rules show first
          // Backend sorting by updatedAt may not work reliably for all cases
          const sortedItems = [...(response.items || [])].sort((a, b) => {
            const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return dateB - dateA; // Newest first
          });
          
          setAuditRules(sortedItems);
        } catch (err) {
          console.error('Error fetching audit data:', err);
        } finally {
          setAuditLoading(false);
        }
      };

      fetchAuditData();
    }
  }, [currentView, selectedStatuses, fetchRules, rulesSearchQuery, startDateFilter, endDateFilter]);

  // Audit Log search effect - backend search fallback when local search finds nothing
  useEffect(() => {
    if (currentView === 'audit-log' && auditSearchQuery.trim().length >= 3) {
      // Debounce search
      const timer = setTimeout(async () => {
        // First check if local search has results
        const localResults = groupedAuditRules.filter(group => 
          group.ruleName.toLowerCase().includes(auditSearchQuery.toLowerCase())
        );
        
        // If no local results, search backend
        if (localResults.length === 0) {

          setIsAuditBackendSearch(true);
          setAuditLoading(true);
          
          try {
            const response = await getRules({ 
              ruleStatus: 'all',
              name: auditSearchQuery.trim(),
              orderBy: 'updatedAt',
              sortOrder: 'DESC',
              limit: 50
            });
            setAuditRules(response.items || []);

          } catch (err) {
            console.error('Error in audit backend search:', err);
          } finally {
            setAuditLoading(false);
          }
        } else {
          setIsAuditBackendSearch(false);
        }
      }, 500); // 500ms debounce
      
      return () => clearTimeout(timer);
    } else if (currentView === 'audit-log' && auditSearchQuery.trim().length === 0 && isAuditBackendSearch) {
      // Clear search - reload initial 100 rules
      setIsAuditBackendSearch(false);
      const fetchAuditData = async () => {
        setAuditLoading(true);
        try {
          const response = await getRules({ 
            ruleStatus: 'all',
            limit: 100,
            offset: 0,
            orderBy: 'updatedAt',
            sortOrder: 'DESC'
          });
          
          // Frontend sorting to ensure latest rules show first
          const sortedItems = [...(response.items || [])].sort((a, b) => {
            const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return dateB - dateA;
          });
          
          setAuditRules(sortedItems);
        } catch (err) {
          console.error('Error fetching audit data:', err);
        } finally {
          setAuditLoading(false);
        }
      };
      fetchAuditData();
    }
  }, [auditSearchQuery, currentView, groupedAuditRules, isAuditBackendSearch]);

  // Docs view - only show when a specific sub-section is selected
  if (currentView.startsWith('docs:')) {
    const section = currentView.split(':')[1];
    return <DocsPage key={currentView} onNavigate={(view: string) => onViewChange?.(view)} initialSection={section} />;
  }

  // Release Notes view
  if (currentView === 'release-notes') {
    return <ReleaseNotes />;
  }

  // FAQs view
  if (currentView === 'faqs') {
    return <FAQsPage onNavigate={(view: string) => onViewChange?.(view)} />;
  }

  // Home view (CPP Overview)
  if (currentView === 'home' || currentView === '') {
    return (
      <Box
        sx={{
          flex: 1,
          pt: 3,
          px: 3,
          pb: 3,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            background: '#fff',
            width: '90%',
            maxWidth: '1400px',
            height: 'fit-content',
            maxHeight: '95%',
            overflow: 'hidden'
          }}
        >
          {/* QR Redirect Section */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
              mb: 0.5,
              fontSize: '22px',
              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
            }}
          >
            QR Redirect
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#333',
              mb: 2.5,
              fontSize: '16px'
            }}
          >
            Create smart redirect rules that dynamically route QR code scans to the right destination
          </Typography>

          {/* QR Redirect Visual Flow Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              textAlign: 'center',
              mb: 2
            }}
          >
            {/* Visual Flow */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 3, mb: 2.5 }}>
              {/* Step 1: Scan */}
              <Box sx={{ textAlign: 'center', maxWidth: 140 }}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    backgroundColor: '#E3F2FD',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    border: '2px solid #2196F3'
                  }}
                >
                  <QrCode2Icon sx={{ fontSize: 34, color: '#2196F3' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#000', fontWeight: 700, mb: 0.3, fontSize: '13px' }}>
                  Scan QR Code
                </Typography>
                <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', lineHeight: 1.3, display: 'block' }}>
                  Consumer scans a Nike QR code on product packaging
                </Typography>
              </Box>

              <ArrowForwardIcon sx={{ fontSize: 28, color: '#2196F3', mt: 2.5 }} />

              {/* Step 2: Rules */}
              <Box sx={{ textAlign: 'center', maxWidth: 140 }}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    backgroundColor: '#FFF3E0',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    border: '2px solid #FF9800'
                  }}
                >
                  <RuleIcon sx={{ fontSize: 34, color: '#FF9800' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#000', fontWeight: 700, mb: 0.3, fontSize: '13px' }}>
                  Rules Applied
                </Typography>
                <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', lineHeight: 1.3, display: 'block' }}>
                  System matches by location, time, and product criteria
                </Typography>
              </Box>

              <ArrowForwardIcon sx={{ fontSize: 28, color: '#2196F3', mt: 2.5 }} />

              {/* Step 3: Destination */}
              <Box sx={{ textAlign: 'center', maxWidth: 140 }}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    backgroundColor: '#E8F5E9',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    border: '2px solid #4CAF50'
                  }}
                >
                  <RocketLaunchIcon sx={{ fontSize: 34, color: '#4CAF50' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#000', fontWeight: 700, mb: 0.3, fontSize: '13px' }}>
                  Destination
                </Typography>
                <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', lineHeight: 1.3, display: 'block' }}>
                  Consumer is redirected to the targeted experience
                </Typography>
              </Box>
            </Box>

            {/* CTA Button */}
            <Button
              variant="contained"
              size="medium"
              onClick={() => onViewChange?.('my-rules')}
              sx={{
                backgroundColor: '#2196F3',
                color: '#fff',
                fontSize: '15px',
                fontWeight: 600,
                px: 4,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                '&:hover': {
                  backgroundColor: '#1976D2',
                  boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)'
                }
              }}
            >
              View & Manage Rules →
            </Button>
          </Paper>

          {/* Feature Highlights */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0 }}>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}
            >
              <PublicIcon sx={{ fontSize: 24, color: '#2196F3', mb: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000', fontSize: '13px' }}>Location Based</Typography>
              <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', display: 'block', lineHeight: 1.2 }}>Redirect users based on their country or region</Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}
            >
              <CategoryIcon sx={{ fontSize: 24, color: '#E91E63', mb: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000', fontSize: '13px' }}>Product Based</Typography>
              <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', display: 'block', lineHeight: 1.2 }}>Target by style, GTIN, brand, division, or category</Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}
            >
              <ScheduleIcon sx={{ fontSize: 24, color: '#FF9800', mb: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000', fontSize: '13px' }}>Time Based</Typography>
              <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', display: 'block', lineHeight: 1.2 }}>Schedule rules with start and end dates</Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}
            >
              <GroupWorkIcon sx={{ fontSize: 24, color: '#9C27B0', mb: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000', fontSize: '13px' }}>Bulk Upload</Typography>
              <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', display: 'block', lineHeight: 1.2 }}>Import multiple rules via Excel</Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 24, color: '#4CAF50', mb: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000', fontSize: '13px' }}>Approvals</Typography>
              <Typography variant="caption" sx={{ color: '#333', fontSize: '12px', display: 'block', lineHeight: 1.2 }}>Review and approve rule changes</Typography>
            </Paper>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Audit Log view
  if (currentView === 'audit-log') {
    return (
      <Box sx={{ flex: 1, p: 4, background: '#fff', minHeight: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#000',
                fontWeight: 700
              }}
            >
              Audit Log
            </Typography>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder="Search by rule name..."
            value={auditSearchQuery}
            onChange={(e) => {
              setAuditSearchQuery(e.target.value);
              setAuditPage(0); // Reset to first page on search
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: '#666', mr: 1, fontSize: '20px' }} />
              ),
            }}
            sx={{
              width: '350px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fafafa',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                }
              }
            }}
          />
          {auditSearchQuery && (
            <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
              Found {groupedAuditRules.length} rule{groupedAuditRules.length !== 1 ? 's' : ''}
              {isAuditBackendSearch && ' (searched all rules in database)'}
            </Typography>
          )}
        </Box>

        {auditLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Timeline Cards - Grouped by Rule */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {paginatedAuditRules.map((ruleGroup) => (
                <Accordion
                  key={ruleGroup.ruleId}
                  sx={{
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: '0 !important'
                    }
                  }}
                >
                  {/* Card Header */}
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: '#fafafa',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: '#f0f0f0'
                      },
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1
                      }
                    }}
                  >
                    {/* Left side: Rule name and event count */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#000',
                          fontSize: '16px',
                          fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                          maxWidth: '300px',
                          wordWrap: 'break-word',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {ruleGroup.ruleName}
                      </Typography>
                      <Chip
                        label={`${ruleGroup.totalEvents} ${ruleGroup.totalEvents === 1 ? 'event' : 'events'}`}
                        size="small"
                        sx={{
                          backgroundColor: '#2196F3',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '12px'
                        }}
                      />
                    </Box>

                    {/* Right side: Latest event preview and stats */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mr: 2 }}>
                      {(() => {
                        // Get latest event (first in sorted array since sorted newest to oldest)
                        const latestEvent = ruleGroup.events[0];
                        
                        // Count event types (badge labels) - matching badge logic
                        const eventTypeCounts: { [key: string]: number } = {};
                        ruleGroup.events.forEach((e: any) => {
                          let eventType = '';
                          // Same logic as the badge display
                          if (e.operationType === 'DELETE') {
                            eventType = 'deleted';
                          } else if (e.status === 'APPROVED') {
                            eventType = 'approved';  // All APPROVED status show as approved
                          } else if (e.status === 'REJECTED') {
                            eventType = 'rejected';  // All REJECTED status show as rejected
                          } else if (e.operationType === 'UPDATE' && (e.status === 'REQUESTED' || e.status === 'UPDATE')) {
                            eventType = 'updated';
                          } else if (e.operationType === 'INSERT' && (e.status === 'REQUESTED' || e.status === 'INSERT' || e.status === 'CREATED')) {
                            eventType = 'insert';
                          }
                          
                          if (eventType) {
                            eventTypeCounts[eventType] = (eventTypeCounts[eventType] || 0) + 1;
                          }
                        });
                        
                        return (
                          <>
                            {/* Latest event preview */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#666',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                }}
                              >
                                Latest:
                              </Typography>
                              <Chip
                                label={
                                  latestEvent.operationType === 'DELETE' ? 'DELETED' :
                                  latestEvent.status === 'APPROVED' ? 'APPROVED' :
                                  latestEvent.status === 'REJECTED' ? 'REJECTED' :
                                  (latestEvent.operationType === 'UPDATE' && (latestEvent.status === 'REQUESTED' || latestEvent.status === 'UPDATE')) ? 'UPDATED' :
                                  (latestEvent.operationType === 'INSERT' && (latestEvent.status === 'REQUESTED' || latestEvent.status === 'INSERT' || latestEvent.status === 'CREATED')) ? 'INSERT' :
                                  latestEvent.operationType
                                }
                                size="small"
                                sx={{
                                  backgroundColor: 
                                    latestEvent.operationType === 'DELETE' ? '#f44336' :
                                    latestEvent.status === 'APPROVED' ? '#4caf50' :
                                    latestEvent.status === 'REJECTED' ? '#f44336' :
                                    (latestEvent.operationType === 'UPDATE' && (latestEvent.status === 'REQUESTED' || latestEvent.status === 'UPDATE')) ? '#ff9800' :
                                    (latestEvent.operationType === 'INSERT' && (latestEvent.status === 'REQUESTED' || latestEvent.status === 'INSERT' || latestEvent.status === 'CREATED')) ? '#2196F3' :
                                    '#9e9e9e',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '10px',
                                  height: '18px'
                                }}
                              />
                            </Box>

                            {/* Event type summary */}
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#666',
                                fontSize: '12px',
                                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                              }}
                            >
                              {[
                                eventTypeCounts.insert && `${eventTypeCounts.insert} inserted`,
                                eventTypeCounts.updated && `${eventTypeCounts.updated} updated`,
                                eventTypeCounts.approved && `${eventTypeCounts.approved} approved`,
                                eventTypeCounts.rejected && `${eventTypeCounts.rejected} rejected`,
                                eventTypeCounts.deleted && `${eventTypeCounts.deleted} deleted`
                              ].filter(Boolean).join(', ')}
                            </Typography>

                            {/* Last activity date */}
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#666',
                                fontSize: '12px',
                                fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                              }}
                            >
                              {new Date(latestEvent.timestamp * 1000).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata' })}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  </AccordionSummary>

                  {/* Timeline Body */}
                  <AccordionDetails sx={{ p: 3, backgroundColor: '#fff' }}>
                    <Box sx={{ position: 'relative', pl: 4 }}>
                      {/* Timeline vertical line */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '8px',
                          top: '12px',
                          bottom: '12px',
                          width: '2px',
                          backgroundColor: '#e0e0e0'
                        }}
                      />

                      {/* Timeline events */}
                      {ruleGroup.events.map((event: any, idx: number) => {
                        // Determine dot color matching badge logic
                        const dotColor = 
                          // DELETE = Red
                          event.operationType === 'DELETE' ? '#f44336' :
                          // APPROVED status = Green (for INSERT or UPDATE)
                          event.status === 'APPROVED' ? '#4caf50' :
                          // UPDATE (REQUESTED) = Orange
                          event.operationType === 'UPDATE' && event.status === 'REQUESTED' ? '#ff9800' :
                          // INSERT (REQUESTED) = Blue
                          event.operationType === 'INSERT' && event.status === 'REQUESTED' ? '#2196F3' :
                          // REJECTED = Red
                          event.status === 'REJECTED' ? '#f44336' :
                          // Default gray
                          '#9e9e9e';
                        
                        // Parse changes for detailed display
                        let parsedChanges = null;
                        
                        if (event.changes) {
                          try {
                            parsedChanges = typeof event.changes === 'string' 
                              ? JSON.parse(event.changes) 
                              : event.changes;
                          } catch (e) {
                            // Parsing failed, parsedChanges stays null
                          }
                        }

                        return (
                          <Box
                            key={idx}
                            sx={{
                              position: 'relative',
                              pb: idx < ruleGroup.events.length - 1 ? 3 : 0,
                              pl: 3
                            }}
                          >
                            {/* Timeline dot */}
                            <Box
                              sx={{
                                position: 'absolute',
                                left: '-4px',
                                top: '4px',
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: dotColor,
                                border: '2px solid #fff',
                                boxShadow: '0 0 0 2px ' + dotColor
                              }}
                            />

                            {/* Event content */}
                            <Box>
                              {/* Event header with date and status tag */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#000',
                                    fontSize: '14px',
                                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                  }}
                                >
                                   {new Date(event.timestamp * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#666',
                                    fontSize: '12px'
                                  }}
                                >
                                  -
                                </Typography>
                                <Chip
                                  label={
                                    event.status === 'CREATED' ? 'INSERT' :
                                    event.status === 'REQUESTED' && event.operationType === 'INSERT' ? 'INSERT' :
                                    event.status === 'REQUESTED' && event.operationType === 'UPDATE' ? 'UPDATED' :
                                    event.status.toUpperCase()
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: 
                                      // INSERT/CREATED = Blue (rule created)
                                      (event.status === 'INSERT' || event.status === 'CREATED' || (event.status === 'REQUESTED' && event.operationType === 'INSERT')) ? '#2196F3' :
                                      // APPROVED = Green
                                      (event.status === 'APPROVED') ? '#4caf50' :
                                      // UPDATE = Orange (rule updated)
                                      (event.status === 'UPDATE' || (event.status === 'REQUESTED' && event.operationType === 'UPDATE')) ? '#ff9800' :
                                      // DELETE = Red (rule deleted)
                                      (event.status === 'DELETE') ? '#f44336' :
                                      // REJECTED = Red
                                      (event.status === 'REJECTED') ? '#f44336' :
                                      // Default gray
                                      '#9e9e9e',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '11px',
                                    height: '22px',
                                    textTransform: 'uppercase'
                                  }}
                                />
                              </Box>

                              {/* Event details - simplified format */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                {/* DELETE: Show Deleted by only (skip other logic) */}
                                {event.operationType === 'DELETE' ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#555',
                                      fontSize: '13px',
                                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                    }}
                                  >
                                    Deleted by: <strong>{formatCreatorName(event.requestedBy || event.modifiedBy || 'Unknown')}</strong>
                                  </Typography>
                                ) : (
                                  <>
                                    {/* INSERT/CREATED or REQUESTED+INSERT: Show Created by */}
                                    {(event.status === 'INSERT' || event.status === 'CREATED' || (event.status === 'REQUESTED' && event.operationType === 'INSERT')) && event.requestedBy && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#555',
                                          fontSize: '13px',
                                          fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                        }}
                                      >
                                        Created by: <strong>{formatCreatorName(event.requestedBy)}</strong>
                                      </Typography>
                                    )}
                                    
                                    {/* APPROVED: Show Requested by and Approved by */}
                                    {event.status === 'APPROVED' && (
                                      <>
                                        {event.requestedBy && (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: '#555',
                                              fontSize: '13px',
                                              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                            }}
                                          >
                                            Requested by: <strong>{formatCreatorName(event.requestedBy)}</strong>
                                          </Typography>
                                        )}
                                        {event.modifiedBy && (
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: '#555',
                                              fontSize: '13px',
                                              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                            }}
                                          >
                                            Approved by: <strong>{formatCreatorName(event.modifiedBy)}</strong>
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                    
                                    {/* UPDATE or REQUESTED+UPDATE: Show Updated by */}
                                    {(event.status === 'UPDATE' || (event.status === 'REQUESTED' && event.operationType === 'UPDATE')) && event.requestedBy && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#555',
                                          fontSize: '13px',
                                          fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                        }}
                                      >
                                        Updated by: <strong>{formatCreatorName(event.requestedBy)}</strong>
                                      </Typography>
                                    )}
                                    
                                    {/* REJECTED: Show Rejected by */}
                                    {event.status === 'REJECTED' && event.modifiedBy && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#555',
                                          fontSize: '13px',
                                          fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                        }}
                                      >
                                        Rejected by: <strong>{formatCreatorName(event.modifiedBy)}</strong>
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Box>

                              {/* Status message */}
                              {event.operationType === 'DELETE' ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: '13px',
                                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                    mt: 0.3
                                  }}
                                >
                                  Rule deleted
                                </Typography>
                              ) : (event.status === 'INSERT' || event.status === 'CREATED' || (event.status === 'REQUESTED' && event.operationType === 'INSERT')) ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: '13px',
                                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                    mt: 0.3
                                  }}
                                >
                                  Rule created
                                </Typography>
                              ) : (event.status === 'UPDATE' || (event.status === 'REQUESTED' && event.operationType === 'UPDATE')) ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: '13px',
                                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                    mt: 0.3
                                  }}
                                >
                                  Rule updated
                                </Typography>
                              ) : event.status === 'APPROVED' ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: '13px',
                                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                    mt: 0.3
                                  }}
                                >
                                  Rule approved
                                </Typography>
                              ) : event.status === 'DELETE' ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: '13px',
                                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                    mt: 0.3
                                  }}
                                >
                                  Rule deleted
                                </Typography>
                              ) : event.status === 'REJECTED' ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#666',
                                    fontSize: '13px',
                                    fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                    mt: 0.3
                                  }}
                                >
                                  Rule rejected
                                </Typography>
                              ) : null}
                              
                              {/* UPDATE REQUESTED: Show modified fields */}
                              {event.operationType === 'UPDATE' && (event.status === 'REQUESTED' || event.status === 'UPDATE') && parsedChanges && (
                                <Box sx={{ mt: 0.3 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#555',
                                      fontSize: '13px',
                                      mb: 0.3,
                                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                    }}
                                  >
                                    Modified fields: <strong>{Object.keys(parsedChanges).join(', ')}</strong>
                                  </Typography>
                                  <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {Object.entries(parsedChanges).map(([field, value]: [string, any]) => {
                                      const hasOldNew = value && typeof value === 'object' && ('old' in value || 'new' in value);
                                      
                                      if (!hasOldNew) return null;
                                      
                                      const valueObj = value as any;
                                      
                                      // Helper to format values with country names and dates
                                      const formatAuditValue = (val: any): string => {
                                        if (val === null || val === undefined) return 'None';
                                        if (Array.isArray(val)) {
                                          if (field === 'countries') {
                                            return val.map(code => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code).join(', ');
                                          }
                                          return val.join(', ');
                                        }
                                        if (typeof val === 'object') return JSON.stringify(val);
                                        
                                        // Check if this is a date/time field and value is a UTC timestamp
                                        if (field === 'ruleStartFrom' || field === 'ruleEndAt') {
                                          const utcTimestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
                                          if (typeof val === 'string' && utcTimestampRegex.test(val)) {
                                            return formatToLocalTime(val);
                                          }
                                        }
                                        
                                        return String(val);
                                      };
                                      
                                      // For arrays, calculate diff and show description
                                      if (Array.isArray(valueObj.old) && Array.isArray(valueObj.new)) {
                                        const oldItems = valueObj.old || [];
                                        const newItems = valueObj.new || [];
                                        const added = newItems.filter((item: any) => !oldItems.includes(item));
                                        const removed = oldItems.filter((item: any) => !newItems.includes(item));
                                        
                                        const changeDesc: string[] = [];
                                        if (removed.length > 0) {
                                          const removedFormatted = field === 'countries'
                                            ? removed.map((code: any) => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code).join(', ')
                                            : removed.join(', ');
                                          changeDesc.push(`Removed: ${removedFormatted}`);
                                        }
                                        if (added.length > 0) {
                                          const addedFormatted = field === 'countries'
                                            ? added.map((code: any) => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code).join(', ')
                                            : added.join(', ');
                                          changeDesc.push(`Added: ${addedFormatted}`);
                                        }
                                        
                                        return (
                                          <Box key={field} sx={{ mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: 0.2 }}>
                                              {field}:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '11px', color: '#f44336', ml: 1 }}>
                                              <strong>Old:</strong> {formatAuditValue(valueObj.old)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '11px', color: '#4caf50', ml: 1, mb: 0.2 }}>
                                              <strong>New:</strong> {formatAuditValue(valueObj.new)}
                                            </Typography>
                                            {changeDesc.length > 0 && (
                                              <Typography variant="body2" sx={{ fontSize: '10px', color: '#666', fontStyle: 'italic', ml: 2 }}>
                                                ({changeDesc.join(' | ')})
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      } else {
                                        // For non-arrays, show old and new
                                        return (
                                          <Box key={field} sx={{ mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600, color: '#555', mb: 0.2 }}>
                                              {field}:
                                            </Typography>
                                            {valueObj.old !== undefined && (
                                              <Typography variant="body2" sx={{ fontSize: '11px', color: '#f44336', ml: 1 }}>
                                                <strong>Old:</strong> {formatAuditValue(valueObj.old)}
                                              </Typography>
                                            )}
                                            {valueObj.new !== undefined && (
                                              <Typography variant="body2" sx={{ fontSize: '11px', color: '#4caf50', ml: 1 }}>
                                                <strong>New:</strong> {formatAuditValue(valueObj.new)}
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      }
                                    }).filter(Boolean)}
                                  </Box>
                                  {/* Rule updated message */}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#666',
                                      fontSize: '13px',
                                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
                                      mt: 0.5
                                    }}
                                  >
                                    Rule updated
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* No changes fallback */}
                              {event.operationType === 'UPDATE' && !parsedChanges && (
                                <Box sx={{ mt: 0.3 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#666',
                                      fontSize: '13px',
                                      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
                                    }}
                                  >
                                    Rule updated
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {!auditLoading && groupedAuditRules.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 3,
                px: 2
              }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Showing {auditPage * auditRowsPerPage + 1}-{Math.min((auditPage + 1) * auditRowsPerPage, groupedAuditRules.length)} of {groupedAuditRules.length} rules
                </Typography>
                <Pagination 
                  count={totalAuditPages} 
                  page={auditPage + 1} 
                  onChange={(event, newPage) => setAuditPage(newPage - 1)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {!auditLoading && groupedAuditRules.length === 0 && (
              <Box sx={{ 
                textAlign: 'center', 
                p: 8,
                backgroundColor: '#fafafa',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                  {auditSearchQuery 
                    ? `No rules found matching "${auditSearchQuery}"`
                    : 'No audit history available'
                  }
                </Typography>
                {auditSearchQuery && (
                  <Button 
                    variant="outlined" 
                    onClick={() => setAuditSearchQuery('')}
                    sx={{ mt: 2 }}
                  >
                    Clear Search
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }

  // My Rules view OR Approvals view
  if (currentView === 'my-rules' || currentView === 'approvals') {
    const title = currentView === 'my-rules' ? 'Rules' : 'Approvals';
    
    return (
      <Box
        sx={{
          flex: 1,
          p: 4,
          background: '#fff',
          minHeight: '100%'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#000',
                fontWeight: 700
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Import & Export Buttons - Only show in my-rules view */}
          {currentView === 'my-rules' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Open Adobe Analytics" placement="bottom" arrow>
                <Button
                  variant="outlined"
                  startIcon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect x="4" y="12" width="4" height="8" rx="0.5" fill="#4CAF50" />
                      <rect x="10" y="6" width="4" height="14" rx="0.5" fill="#F44336" />
                      <rect x="16" y="2" width="4" height="18" rx="0.5" fill="#1A237E" />
                    </svg>
                  }
                  onClick={handleOpenAdobeAnalytics}
                  sx={{
                    borderColor: '#E03C31',
                    color: '#E03C31',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#C62828',
                      backgroundColor: 'rgba(224, 60, 49, 0.08)',
                      color: '#C62828'
                    }
                  }}
                >
                  Adobe Analytics
                </Button>
              </Tooltip>
              <Tooltip title="Download Rule Upload Format" placement="bottom" arrow>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  sx={{
                    borderColor: '#7B1FA2',
                    color: '#7B1FA2',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#6A1B9A',
                      backgroundColor: 'rgba(123, 31, 162, 0.08)',
                      color: '#6A1B9A'
                    }
                  }}
                >
                  Download Format
                </Button>
              </Tooltip>
              <Tooltip title="Import Rules from Excel" placement="bottom" arrow>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={handleImportClick}
                  sx={{
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#388E3C',
                      backgroundColor: 'rgba(76, 175, 80, 0.08)',
                      color: '#388E3C'
                    }
                  }}
                >
                  Import
                </Button>
              </Tooltip>
              <Tooltip title="Export All Rules to CSV" placement="bottom" arrow>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportRules}
                    disabled={sortedRules.length === 0}
                    sx={{
                      borderColor: '#2196F3',
                      color: '#2196F3',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#1976D2',
                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        color: '#1976D2'
                      },
                      '&.Mui-disabled': {
                        borderColor: '#ccc',
                        color: '#999'
                      }
                    }}
                  >
                    Export
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Search and Date Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search Bar */}
          <TextField
            size="small"
            placeholder="Search by rule name..."
            value={rulesSearchQuery}
            onChange={(e) => {
              setRulesSearchQuery(e.target.value);
              setPage(0); // Reset to first page
            }}
            helperText={
              rulesSearchQuery.trim().length > 0 && rulesSearchQuery.trim().length < 3
                ? 'Type at least 3 characters to search'
                : ''
            }
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: '#666', mr: 1, fontSize: '20px' }} />
              ),
              endAdornment: loading && rulesSearchQuery.trim().length >= 3 ? (
                <CircularProgress size={20} sx={{ color: '#2196F3' }} />
              ) : null,
            }}
            sx={{
              width: '300px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fafafa',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                }
              },
              '& .MuiFormHelperText-root': {
                fontSize: '11px',
                color: '#ff9800',
                marginLeft: 0
              }
            }}
          />

          {/* Date Filters - Only show in my-rules view */}
          {currentView === 'my-rules' && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDateFilter ? dayjs(startDateFilter) : null}
                onChange={(newValue: Dayjs | null) => {
                  setStartDateFilter(newValue ? newValue.format('YYYY-MM-DD') : '');
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: '180px' }
                  }
                }}
              />
              
              <DatePicker
                label="End Date"
                value={endDateFilter ? dayjs(endDateFilter) : null}
                onChange={(newValue: Dayjs | null) => {
                  setEndDateFilter(newValue ? newValue.format('YYYY-MM-DD') : '');
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: '180px' }
                  }
                }}
                minDate={startDateFilter ? dayjs(startDateFilter) : undefined}
              />
            </LocalizationProvider>
          )}

          {/* Clear Filters Button */}
          {(rulesSearchQuery || (currentView === 'my-rules' && (startDateFilter || endDateFilter))) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setRulesSearchQuery('');
                if (currentView === 'my-rules') {
                  setStartDateFilter('');
                  setEndDateFilter('');
                }
                setPage(0);
              }}
              sx={{
                textTransform: 'none',
                color: '#666',
                borderColor: '#ccc',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Clear Filters
            </Button>
          )}
          
          {/* Results Count */}
          {(rulesSearchQuery || (currentView === 'my-rules' && (startDateFilter || endDateFilter))) && !loading && (
            <Typography variant="caption" sx={{ color: '#666', ml: 1 }}>
              Found {sortedRules.length} rule{sortedRules.length !== 1 ? 's' : ''}
              {rulesSearchQuery.trim().length >= 3 && ' (searched across all statuses)'}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, backgroundColor: '#ffebee', borderRadius: 2 }}>
            <Typography color="error">{error}</Typography>
            <Button onClick={() => fetchRules(rulesSearchQuery)} sx={{ mt: 2 }}>Retry</Button>
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e8e8e8' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#000' }}>Name</TableCell>
                  {currentView === 'my-rules' ? (
                    <TableCell sx={{ fontWeight: 700, color: '#000' }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5, 
                          cursor: 'pointer',
                          '&:hover': {
                            '& .MuiSvgIcon-root': {
                              color: '#2196F3'
                            }
                          }
                        }} 
                        onClick={handleDropdownOpen}
                      >
                        <Typography sx={{ fontWeight: 700, color: '#000' }}>Status</Typography>
                        <TuneIcon 
                          sx={{ 
                            fontSize: '20px', 
                            color: '#666', 
                            transition: 'color 0.2s'
                          }} 
                        />
                      </Box>
                      <Menu
                        anchorEl={dropdownAnchor}
                        open={dropdownOpen}
                        onClose={handleDropdownClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          sx: {
                            width: 140,
                            maxWidth: 140,
                            '& .MuiList-root': {
                              padding: 0
                            },
                            '& .MuiMenuItem-root': {
                              minHeight: 32,
                              py: 0.3,
                              fontSize: '13px'
                            }
                          }
                        }}
                      >
                        {statusOptions.map((status) => (
                          <MenuItem 
                            key={status} 
                            value={status}
                            onClick={() => {
                              const currentIndex = tempSelectedStatuses.indexOf(status);
                              const newSelected = [...tempSelectedStatuses];
                              
                              if (currentIndex === -1) {
                                newSelected.push(status);
                              } else {
                                newSelected.splice(currentIndex, 1);
                              }
                              setTempSelectedStatuses(newSelected);
                            }}
                          >
                            <Checkbox checked={tempSelectedStatuses.indexOf(status) > -1} />
                            <ListItemText primary={status} />
                          </MenuItem>
                        ))}
                        <Box sx={{ display: 'flex', gap: 0.5, p: 0.8, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                          <Button 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClear();
                            }}
                            sx={{ 
                              flex: 1,
                              textTransform: 'none',
                              borderColor: '#ccc',
                              color: '#666',
                              fontSize: '11px',
                              py: 0.3,
                              px: 1,
                              minHeight: 24,
                              minWidth: 50
                            }}
                            variant="outlined"
                          >
                            Clear
                          </Button>
                          <Button 
                            size="small" 
                            variant="contained" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApply();
                            }}
                            sx={{ 
                              flex: 1,
                              textTransform: 'none',
                              backgroundColor: '#2196F3',
                              fontSize: '11px',
                              py: 0.3,
                              px: 1,
                              minHeight: 24,
                              minWidth: 50,
                              '&:hover': {
                                backgroundColor: '#1976D2'
                              }
                            }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Menu>
                    </TableCell>
                  ) : (
                    <TableCell sx={{ fontWeight: 700, color: '#000' }}>
                      {currentView === 'approvals' ? 'Approval Status' : 'Status'}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, color: '#000' }}>Countries</TableCell>
                  <TableCell 
                    onClick={handleSortByCreated}
                    sx={{ 
                      fontWeight: 700, 
                      color: '#000',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: '#d0d0d0'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {currentView === 'approvals' ? 'Requested' : 'Created'}
                      {sortBy === 'createdAt' && (
                        <span style={{ fontSize: '12px' }}>
                          {sortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000' }}>
                    {currentView === 'approvals' ? 'Requested By' : 'Created By'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRules.map((rule, index) => (
                  <TableRow 
                    key={rule.ruleId}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fff' },
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell sx={{ 
                      color: '#000', 
                      fontWeight: 600,
                      maxWidth: '300px',
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      {rule.name}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Show database status as-is (don't calculate based on dates)
                        // Status updates are handled by backend scheduler, not frontend
                        const displayStatus = currentView === 'approvals' 
                          ? (rule.approvalStatus || 'Unknown')
                          : (rule.ruleStatus || 'Unknown');
                        
                        return (
                          <Chip 
                            label={displayStatus}
                            size="small"
                            sx={{
                              backgroundColor: currentView === 'approvals' 
                                ? (rule.approvalStatus === 'pending' ? '#ff9800' : 
                                   rule.approvalStatus === 'approved' ? '#4caf50' : 
                                   rule.approvalStatus === 'rejected' ? '#f44336' : '#9e9e9e')
                                : getStatusColor(displayStatus.toLowerCase()),
                              color: '#fff',
                              fontWeight: 600,
                              textTransform: 'uppercase'
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ color: '#666' }}>
                      {rule.countries && rule.countries.length > 0 ? (
                        <Tooltip 
                          title={
                            rule.countries
                              .map(code => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code)
                              .join(', ')
                          }
                          placement="top"
                          arrow
                        >
                          <Box sx={{ cursor: 'pointer', display: 'inline-block' }}>
                            {rule.countries.length}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ display: 'inline-block' }}>
                          Global
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#666' }}>
                      {rule.createdAt ? new Date(rule.createdAt).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata' }) : '-'}
                    </TableCell>
                    <TableCell sx={{ color: '#666' }}>
                      {currentView === 'approvals' 
                        ? formatCreatorName(rule.pendingChanges?.requestedBy || rule.createdBy || 'Unknown')
                        : formatCreatorName(rule.createdBy || 'Unknown')
                      }
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {currentView === 'approvals' ? (
                          <>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleViewClick(rule)}
                              sx={{ 
                                borderColor: '#000',
                                color: '#000',
                                '&:hover': {
                                  borderColor: '#000',
                                  backgroundColor: 'rgba(0,0,0,0.05)'
                                }
                              }}
                            >
                              View
                            </Button>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => {

                                handleApprove(rule);
                              }}
                              sx={{ 
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                cursor: 'pointer',
                                '&:hover': {
                                  borderColor: '#4caf50',
                                  backgroundColor: 'rgba(76,175,80,0.08)'
                                }
                              }}
                            >
                              Approve
                            </Button>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleReject(rule)}
                              sx={{ 
                                borderColor: '#f44336',
                                color: '#f44336',
                                '&:hover': {
                                  borderColor: '#f44336',
                                  backgroundColor: 'rgba(244,67,54,0.08)'
                                }
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleViewClick(rule)}
                              sx={{ 
                                borderColor: '#000',
                                color: '#000',
                                '&:hover': {
                                  borderColor: '#000',
                                  backgroundColor: 'rgba(0,0,0,0.05)'
                                }
                              }}
                            >
                              View
                            </Button>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleEditClick(rule)}
                              sx={{ 
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                  borderColor: '#2196F3',
                                  backgroundColor: 'rgba(33, 150, 243, 0.08)'
                                }
                              }}
                            >
                              Edit
                            </Button>
                            {/* Test Button - Active rules only */}
                            {(() => {
                              const isActive = rule.ruleStatus?.toLowerCase() === 'active';
                              return (
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => {
                                    setRuleToTest(rule);
                                    setShowTestDialog(true);
                                  }}
                                  disabled={!isActive}
                                  sx={{ 
                                    borderColor: isActive ? '#9c27b0' : '#ccc',
                                    color: isActive ? '#9c27b0' : '#ccc',
                                    '&:hover': {
                                      borderColor: isActive ? '#9c27b0' : '#9c27b0',
                                      backgroundColor: isActive ? 'rgba(156,39,176,0.08)' : 'transparent'
                                    },
                                    '&.Mui-disabled': {
                                      borderColor: '#ccc',
                                      color: '#ccc',
                                    }
                                  }}
                                >
                                  Test
                                </Button>
                              );
                            })()}
                            {/* Delete Button - Admin only */}
                            {isAdmin && (() => {
                              const isInactive = rule.ruleStatus?.toLowerCase() === 'inactive';
                              
                              return (
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => handleDelete(rule)}
                                  disabled={isInactive}
                                  sx={{ 
                                    borderColor: isInactive ? '#ccc' : '#f44336',
                                    color: isInactive ? '#ccc' : '#f44336',
                                    '&:hover': {
                                      borderColor: isInactive ? '#ccc' : '#f44336',
                                      backgroundColor: isInactive ? 'transparent' : 'rgba(244,67,54,0.08)'
                                    },
                                    '&.Mui-disabled': {
                                      borderColor: '#ccc',
                                      color: '#ccc',
                                      cursor: 'not-allowed'
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              );
                            })()}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Empty state for filtered results */}
        {!loading && !error && sortedRules.length === 0 && rules.length > 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            p: 8,
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              No rules found matching your filters
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
              {rulesSearchQuery && `Search: "${rulesSearchQuery}"`}
              {currentView === 'my-rules' && startDateFilter && ` • From: ${dayjs(startDateFilter).format('MMM DD, YYYY')}`}
              {currentView === 'my-rules' && endDateFilter && ` • To: ${dayjs(endDateFilter).format('MMM DD, YYYY')}`}
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => {
                setRulesSearchQuery('');
                if (currentView === 'my-rules') {
                  setStartDateFilter('');
                  setEndDateFilter('');
                }
                setPage(0);
              }}
            >
              Clear All Filters
            </Button>
          </Box>
        )}
        
        {!loading && !error && rules.length > 0 && sortedRules.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 2,
            px: 2
          }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, effectiveTotalCount)} of {effectiveTotalCount} rules
            </Typography>
            <Pagination 
              count={totalPages} 
              page={page + 1} 
              onChange={handleChangePage}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        <Dialog 
          open={viewModalOpen} 
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            handleCloseModal();
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#2196F3', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Rule Details
              </Typography>
            </Box>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ mt: 2 }}>
            {selectedRule && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Pending Changes - Show at top if exists */}
                {selectedRule.pendingChanges && (
                  <Box sx={{ bgcolor: '#fff3e0', p: 2.5, borderRadius: 1, border: '2px solid #ff9800' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <WarningIcon sx={{ color: '#ff9800', fontSize: 24 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ff9800', fontSize: '14px' }}>
                        PENDING CHANGES
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, columnGap: 4, mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Operation</Typography>
                        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.pendingChanges.operationType}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Requested By</Typography>
                        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                          {formatCreatorName(selectedRule.pendingChanges.requestedBy)}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Requested At</Typography>
                      <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                        {new Date(selectedRule.pendingChanges.requestedAt).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })} IST
                      </Typography>
                      </Box>
                    </Box>
                    
                    {/* Display Changes */}
                    {selectedRule.pendingChanges.changes && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ffb74d' }}>
                        <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 1 }}>
                          Modified Fields
                        </Typography>
                        <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                          {(() => {
                            try {
                              const changes = typeof selectedRule.pendingChanges.changes === 'string' 
                                ? JSON.parse(selectedRule.pendingChanges.changes) 
                                : selectedRule.pendingChanges.changes;
                              
                              return Object.entries(changes).map(([key, value]: [string, any]) => {
                                // Check if value has old/new structure
                                const hasOldNew = value && typeof value === 'object' && ('old' in value || 'new' in value);
                                
                                const formatValue = (val: any, fieldName?: string): string => {
                                  if (val === null || val === undefined) return 'null';
                                  if (Array.isArray(val)) return val.join(', ');
                                  if (typeof val === 'object') return JSON.stringify(val);
                                  
                                  // Check if this is a date/time field and value is a UTC timestamp
                                  if (fieldName === 'ruleStartFrom' || fieldName === 'ruleEndAt') {
                                    // Check if it's a valid ISO 8601 UTC timestamp
                                    const utcTimestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
                                    if (typeof val === 'string' && utcTimestampRegex.test(val)) {
                                      return formatToLocalTime(val);
                                    }
                                  }
                                  
                                  return String(val);
                                };
                                
                                // Helper to get array differences
                                const getArrayDiff = (oldArr: any[], newArr: any[]) => {
                                  const added = newArr.filter(item => !oldArr.includes(item));
                                  const removed = oldArr.filter(item => !newArr.includes(item));
                                  return { added, removed };
                                };
                                
                                // Skip if both old and new exist and are identical
                                if (hasOldNew && value.old !== undefined && value.new !== undefined) {
                                  // Special handling for date fields - compare only date+time, ignore timezone
                                  if (key === 'ruleStartFrom' || key === 'ruleEndAt') {
                                    try {
                                      const oldDate = new Date(value.old);
                                      const newDate = new Date(value.new);
                                      
                                      // Compare year, month, day, hour, minute (ignore seconds, ms, timezone)
                                      const oldDateStr = `${oldDate.getUTCFullYear()}-${String(oldDate.getUTCMonth() + 1).padStart(2, '0')}-${String(oldDate.getUTCDate()).padStart(2, '0')} ${String(oldDate.getUTCHours()).padStart(2, '0')}:${String(oldDate.getUTCMinutes()).padStart(2, '0')}`;
                                      const newDateStr = `${newDate.getUTCFullYear()}-${String(newDate.getUTCMonth() + 1).padStart(2, '0')}-${String(newDate.getUTCDate()).padStart(2, '0')} ${String(newDate.getUTCHours()).padStart(2, '0')}:${String(newDate.getUTCMinutes()).padStart(2, '0')}`;
                                      
                                      // If date+time are the same, skip this field
                                      if (!isNaN(oldDate.getTime()) && !isNaN(newDate.getTime()) && oldDateStr === newDateStr) {
                                        return null;
                                      }
                                    } catch (e) {
                                      // Fall through to regular comparison if date parsing fails
                                    }
                                  }
                                  
                                  const oldStr = JSON.stringify(value.old);
                                  const newStr = JSON.stringify(value.new);
                                  if (oldStr === newStr) {
                                    return null; // Skip this field - no actual change
                                  }
                                }
                                
                                return (
                                  <Box key={key} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#666', mb: 0.5 }}>
                                      {key}:
                                    </Typography>
                                    {hasOldNew ? (
                                      <Box sx={{ ml: 1 }}>
                                        {(() => {
                                          // For arrays, show complete old and new arrays with description of changes
                                          if (Array.isArray(value.old) && Array.isArray(value.new)) {
                                            const { added, removed } = getArrayDiff(value.old, value.new);
                                            
                                            // Helper to format items (convert country codes to names if applicable)
                                            const formatArrayItems = (items: any[]) => {
                                              if (key === 'countries') {
                                                return items.map(code => 
                                                  COUNTRY_OPTIONS.find(c => c.code === code)?.name || code
                                                ).join(', ');
                                              }
                                              return items.join(', ');
                                            };
                                            
                                            // Build description of what changed (with country names if applicable)
                                            const changeDesc: string[] = [];
                                            if (removed.length > 0) {
                                              const removedFormatted = key === 'countries' 
                                                ? removed.map(code => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code).join(', ')
                                                : removed.join(', ');
                                              changeDesc.push(`Removed: ${removedFormatted}`);
                                            }
                                            if (added.length > 0) {
                                              const addedFormatted = key === 'countries'
                                                ? added.map(code => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code).join(', ')
                                                : added.join(', ');
                                              changeDesc.push(`Added: ${addedFormatted}`);
                                            }
                                            
                                            return (
                                              <>
                                                {/* Show complete old array */}
                                                <Typography variant="body2" sx={{ fontSize: '12px', color: '#f44336', mb: 0.3 }}>
                                                  <strong>Old:</strong> {value.old.length > 0 ? formatArrayItems(value.old) : 'None'}
                                                </Typography>
                                                {/* Show complete new array */}
                                                <Typography variant="body2" sx={{ fontSize: '12px', color: '#4caf50', mb: 0.5 }}>
                                                  <strong>New:</strong> {value.new.length > 0 ? formatArrayItems(value.new) : 'None'}
                                                </Typography>
                                                {/* Show description of what changed */}
                                                {changeDesc.length > 0 && (
                                                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#666', fontStyle: 'italic', ml: 1 }}>
                                                    ({changeDesc.join(' | ')})
                                                  </Typography>
                                                )}
                                              </>
                                            );
                                          }
                                          
                                          // For non-arrays, show full old and new values
                                          return (
                                            <>
                                              {value.old !== undefined && (
                                                <Typography variant="body2" sx={{ fontSize: '12px', color: '#f44336', mb: 0.3 }}>
                                                  <strong>Old:</strong> {formatValue(value.old, key)}
                                                </Typography>
                                              )}
                                              {value.new !== undefined && (
                                                <Typography variant="body2" sx={{ fontSize: '12px', color: '#4caf50' }}>
                                                  <strong>New:</strong> {formatValue(value.new, key)}
                                                </Typography>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#000', ml: 1 }}>
                                        {formatValue(value)}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }).filter(Boolean);
                            } catch (e) {
                              return (
                                <Typography variant="body2" sx={{ fontSize: '13px', fontStyle: 'italic', color: '#666' }}>
                                  Unable to parse changes
                                </Typography>
                              );
                            }
                          })()}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                
                {/* Basic Information - Always show */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#2196F3', fontSize: '14px' }}>
                    BASIC INFORMATION
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, columnGap: 4 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Name</Typography>
                      <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>
                        {currentView === 'approvals' ? 'Approval Status' : 'Status'}
                      </Typography>
                      {(() => {
                        // Show actual database status (not calculated)
                        const displayStatus = currentView === 'approvals' 
                          ? (selectedRule.approvalStatus || 'Unknown')
                          : (selectedRule.ruleStatus || 'Unknown');
                        
                        return (
                          <Chip 
                            label={displayStatus}
                            size="small"
                            sx={{
                              backgroundColor: currentView === 'approvals' 
                                ? (selectedRule.approvalStatus === 'pending' ? '#ff9800' : 
                                   selectedRule.approvalStatus === 'approved' ? '#4caf50' : 
                                   selectedRule.approvalStatus === 'rejected' ? '#f44336' : '#9e9e9e')
                                : getStatusColor(displayStatus.toLowerCase()),
                              color: '#fff',
                              fontWeight: 600,
                              textTransform: 'uppercase'
                            }}
                          />
                        );
                      })()}
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Redirect URL</Typography>
                      <Typography 
                        component="a"
                        href={selectedRule.redirectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2" 
                        sx={{ 
                          fontSize: '14px', 
                          fontWeight: 400, 
                          wordBreak: 'break-all',
                          color: '#2196F3',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#1976D2',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {selectedRule.redirectUrl}
                      </Typography>
                    </Box>
                    {selectedRule.claravineStatus && selectedRule.claravineStatus !== 'not_set' && (
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700 }}>Tracking URL</Typography>
                          <Chip
                            label={selectedRule.claravineStatus === 'completed' ? 'Ready' : selectedRule.claravineStatus === 'pending' ? 'Generating...' : 'Failed'}
                            size="small"
                            sx={{
                              height: '20px',
                              fontSize: '11px',
                              fontWeight: 600,
                              backgroundColor: selectedRule.claravineStatus === 'completed' ? '#e8f5e9' : selectedRule.claravineStatus === 'pending' ? '#fff3e0' : '#ffebee',
                              color: selectedRule.claravineStatus === 'completed' ? '#2e7d32' : selectedRule.claravineStatus === 'pending' ? '#e65100' : '#c62828',
                            }}
                          />
                          {selectedRule.claravineStatus === 'failed' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const sent = sendToClaravineExtension(
                                  selectedRule.ruleId,
                                  selectedRule.redirectUrl,
                                  selectedRule.name
                                );
                                if (sent) {
                                  setSuccessNotifMessage('Retrying Claravine tracking URL generation...');
                                  setShowSuccessNotification(true);
                                } else {
                                  setErrorNotifMessage('Claravine extension not available. Please ensure it is installed.');
                                  setShowErrorNotification(true);
                                }
                              }}
                              sx={{ height: '20px', fontSize: '11px', textTransform: 'none' }}
                            >
                              Retry
                            </Button>
                          )}
                        </Box>
                        {selectedRule.claravineUrl && (
                          <Typography
                            component="a"
                            href={selectedRule.claravineUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              fontSize: '14px',
                              fontWeight: 400,
                              wordBreak: 'break-all',
                              color: '#4caf50',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              '&:hover': { color: '#388e3c' }
                            }}
                          >
                            {selectedRule.claravineUrl}
                          </Typography>
                        )}
                        {selectedRule.claravineStatus === 'failed' && selectedRule.claravineError && (
                          <Typography variant="body2" sx={{ fontSize: '12px', color: '#c62828', mt: 0.5 }}>
                            Error: {selectedRule.claravineError}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Priority</Typography>
                      <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                        {selectedRule.priority === 124 ? 'Critical' :
                         selectedRule.priority === 110 ? 'High' :
                         selectedRule.priority === 100 ? 'Medium' :
                         selectedRule.priority === 80 ? 'Low' :
                         selectedRule.priority}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Targeting Criteria - Only show if any targeting field has values */}
                {(selectedRule.qrs?.length || selectedRule.styleColorCodes?.length || selectedRule.styleCodes?.length || selectedRule.gtins?.length || 
                  selectedRule.countries?.length || selectedRule.categories?.length || selectedRule.divisions?.length ||
                  selectedRule.brandName || selectedRule.athleteFullName || selectedRule.boundingBox) && (
                  <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#2196F3', fontSize: '14px' }}>
                      SELECTED MATCHING CRITERIA
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, columnGap: 4 }}>
                      {selectedRule.qrs && selectedRule.qrs.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>QR Codes</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.qrs.join(', ')}</Typography>
                        </Box>
                      )}
                      
                      {selectedRule.styleColorCodes && selectedRule.styleColorCodes.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Style Color Codes</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.styleColorCodes.join(', ')}</Typography>
                        </Box>
                      )}

                      {selectedRule.styleCodes && selectedRule.styleCodes.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Style Codes</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.styleCodes.join(', ')}</Typography>
                        </Box>
                      )}
                      
                      {selectedRule.gtins && selectedRule.gtins.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>GTINs</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.gtins.join(', ')}</Typography>
                        </Box>
                      )}
                      
                      {selectedRule.countries && selectedRule.countries.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Countries</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                            {selectedRule.countries.map(code => COUNTRY_OPTIONS.find(c => c.code === code)?.name || code).join(', ')}
                          </Typography>
                        </Box>
                      )}
                      
                      {selectedRule.categories && selectedRule.categories.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Categories</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.categories.join(', ')}</Typography>
                        </Box>
                      )}
                      
                      {selectedRule.divisions && selectedRule.divisions.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Divisions</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.divisions.join(', ')}</Typography>
                        </Box>
                      )}
                      
                      {selectedRule.brandName && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Brand Name</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.brandName}</Typography>
                        </Box>
                      )}
                      
                      {selectedRule.athleteFullName && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Athlete</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.athleteFullName}</Typography>
                        </Box>
                      )}
                      
                      {selectedRule.boundingBox && (
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Bounding Box</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                            N: {selectedRule.boundingBox.north}, S: {selectedRule.boundingBox.south}, E: {selectedRule.boundingBox.east}, W: {selectedRule.boundingBox.west}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Schedule - Only show if any schedule field has values */}
                {(selectedRule.ruleStartFrom || selectedRule.ruleEndAt || selectedRule.ttl) && (
                  <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#2196F3', fontSize: '14px' }}>
                      SCHEDULE
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, columnGap: 4 }}>
                      {selectedRule.ruleStartFrom && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Start Date/Time</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                            {formatToLocalTime(selectedRule.ruleStartFrom)}
                          </Typography>
                        </Box>
                      )}
                      
                      {selectedRule.ruleEndAt && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>End Date/Time</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                            {formatToLocalTime(selectedRule.ruleEndAt)}
                          </Typography>
                        </Box>
                      )}
                      
                      {selectedRule.ttl && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>TTL</Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRule.ttl}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Metadata - Always show */}
                <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#2196F3', fontSize: '14px' }}>
                    METADATA
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, columnGap: 4 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Created Date</Typography>
                      <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                        {selectedRule.createdAt ? new Date(selectedRule.createdAt).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata' }) : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Created By</Typography>
                      <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                        {formatCreatorName(selectedRule.createdBy)}
                      </Typography>
                    </Box>
                    
                    {selectedRule.updatedAt && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Updated Date</Typography>
                        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                          {new Date(selectedRule.updatedAt).toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata' })}
                        </Typography>
                      </Box>
                    )}
                    
                    {selectedRule.updatedBy && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#000', fontSize: '13px', fontWeight: 700, mb: 0.5 }}>Updated By</Typography>
                        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 400 }}>
                          {formatCreatorName(selectedRule.updatedBy)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Floating Action Button - Only show in my-rules view */}
        {currentView === 'my-rules' && (
          <Tooltip title="Create Rule" placement="left" arrow>
            <Fab
              color="primary"
              aria-label="create rule"
              onClick={() => navigate('/create-rule')}
              size="small"
              sx={{
                position: 'fixed',
                bottom: 40,
                right: 32,
                width: 44,
                height: 44,
                backgroundColor: '#2196F3',
                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                '&:hover': {
                  backgroundColor: '#1976D2',
                  boxShadow: '0 6px 16px rgba(33, 150, 243, 0.6)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s ease',
                zIndex: 1000
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </Fab>
          </Tooltip>
        )}

        {/* Success Notification */}
        <SuccessNotification
          open={showSuccessNotification}
          message={successNotifMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            fetchRules(); // Refresh rules after dismissing
          }}
        />

        {/* Error Notification */}
        <ErrorNotification
          open={showErrorNotification}
          message={errorNotifMessage}
          onClose={() => setShowErrorNotification(false)}
        />

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          open={showConfirmDialog}
          title="Confirm Approval"
          message="Are you sure you want to approve this rule"
          ruleName={ruleToApprove?.name}
          confirmText="Confirm"
          onConfirm={handleConfirmApprove}
          onCancel={() => {
            setShowConfirmDialog(false);
            setRuleToApprove(null);
          }}
        />

        {/* Rejection Dialog */}
        <RejectionDialog
          open={showRejectDialog}
          ruleName={ruleToReject?.name}
          onConfirm={handleConfirmReject}
          onCancel={() => {
            setShowRejectDialog(false);
            setRuleToReject(null);
          }}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          open={showDeleteDialog}
          title="Confirm Deletion"
          message="Are you sure you want to delete this rule"
          ruleName={ruleToDelete?.name}
          confirmText="Delete"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setRuleToDelete(null);
          }}
        />

        <TestRuleDialog
          open={showTestDialog}
          rule={ruleToTest}
          onClose={() => {
            setShowTestDialog(false);
            setRuleToTest(null);
          }}
        />

        {/* Import Excel Dialog */}
        <Dialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '12px', maxHeight: '85vh' }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Import Rules from Excel
              </Typography>
              {importFileName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <InsertDriveFileIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {importFileName}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton onClick={() => setImportDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {/* Phase 1: Upload zone — visible when no data loaded and not loading */}
            {!importLoading && importData.length === 0 && !importError && (
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: dragActive ? '2px solid #2196F3' : '2px dashed #ccc',
                  borderRadius: '12px',
                  bgcolor: dragActive ? 'rgba(33, 150, 243, 0.06)' : '#FAFAFA',
                  p: 6,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#90CAF9',
                    bgcolor: 'rgba(33, 150, 243, 0.04)',
                  },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 56, color: dragActive ? '#2196F3' : '#BDBDBD', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  {dragActive ? 'Drop your file here' : 'Upload your Excel file'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#888', mb: 2.5 }}>
                  Drag and drop your <strong>.xlsx</strong> or <strong>.xls</strong> file here
                </Typography>
                <Typography variant="body2" sx={{ color: '#AAA', mb: 2 }}>
                  — or —
                </Typography>
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#2196F3',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': { bgcolor: '#1976D2' },
                  }}
                >
                  Browse Files
                </Button>
                <Typography variant="caption" display="block" sx={{ color: '#BBB', mt: 2 }}>
                  Max file size: 10 MB
                </Typography>
              </Box>
            )}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelected}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
            />

            {/* Loading state */}
            {importLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <CircularProgress size={48} sx={{ color: '#2196F3', mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#666' }}>
                  Parsing Excel file...
                </Typography>
              </Box>
            )}

            {/* Error state */}
            {importError && (
              <Box sx={{ mt: importData.length === 0 ? 0 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#FFF3F0', borderRadius: '8px' }}>
                  <ErrorOutlineIcon sx={{ color: '#f44336' }} />
                  <Typography sx={{ color: '#d32f2f' }}>{importError}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { setImportError(null); setImportFileName(''); }}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Try Again
                  </Button>
                </Box>
              </Box>
            )}

            {/* Warnings */}
            {importWarnings.length > 0 && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FFF8E1', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <WarningIcon sx={{ color: '#F9A825', mt: 0.3, fontSize: 20 }} />
                <Box>
                  {importWarnings.map((w, i) => (
                    <Typography key={i} variant="body2" sx={{ color: '#7B6100' }}>{w}</Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Phase 2: Results table */}
            {!importLoading && importData.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      Found <strong>{importData.length}</strong> URL group{importData.length !== 1 ? 's' : ''}. Select the rows you want to create and click "Create Rules" below.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={() => { setImportData([]); setImportWarnings([]); setImportError(null); setImportFileName(''); setSelectedImportRows(new Set()); setBulkCreateResults([]); }}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                  >
                    Upload Different File
                  </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell padding="checkbox" sx={{ width: '42px' }}>
                          <Checkbox
                            size="small"
                            indeterminate={selectedImportRows.size > 0 && selectedImportRows.size < validRowIndices.length}
                            checked={validRowIndices.length > 0 && validRowIndices.every(i => selectedImportRows.has(i))}
                            onChange={handleToggleAllImportRows}
                            disabled={bulkCreating || validRowIndices.length === 0}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '40px' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Rule Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Redirect URL</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Style Color Codes</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Style Codes</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>HTTPS</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>
                          <Tooltip title="Style Color Code pattern valid (e.g. HJ1102-102)" arrow>
                            <span>Style Color Valid</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>
                          <Tooltip title="Style Code pattern valid (e.g. AB1234)" arrow>
                            <span>Style Valid</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importData.map((item, index) => {
                        const rowResult = bulkCreateResults.find((r) => r.index === index);
                        const rowValid = isRowValid(item);
                        return (
                          <TableRow
                            key={index}
                            hover={rowValid}
                            sx={{
                              bgcolor: rowResult
                                ? rowResult.success ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)'
                                : !rowValid ? 'rgba(244, 67, 54, 0.04)' : 'inherit',
                              opacity: rowValid ? 1 : 0.7,
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Tooltip title={!rowValid ? 'This row has validation errors and cannot be selected' : ''} arrow>
                                <span>
                                  <Checkbox
                                    size="small"
                                    checked={selectedImportRows.has(index)}
                                    onChange={() => handleToggleImportRow(index)}
                                    disabled={!rowValid || bulkCreating || !!rowResult?.success}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell sx={{ maxWidth: 160 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#555' }}>
                                {generateRuleName(item, index)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {item.url}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {item.styleColorCodes.map((code, i) => (
                                  <Chip key={i} label={code} size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {item.styleCodes.map((code, i) => (
                                  <Chip key={i} label={code} size="small" color="info" sx={{ fontSize: '0.7rem', height: 22 }} />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {item.startDate || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {item.httpsValid ? (
                                <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                              ) : (
                                <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 18 }} />
                              )}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {item.styleColorCodes.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>—</Typography>
                              ) : allStyleColorCodesValid(item) ? (
                                <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                              ) : (
                                <Tooltip title={`Invalid: ${item.styleColorCodes.filter(c => !isValidStyleColorCode(c)).join(', ')}`} arrow>
                                  <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 18 }} />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {item.styleCodes.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>—</Typography>
                              ) : allStyleCodesValid(item) ? (
                                <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                              ) : (
                                <Tooltip title={`Invalid: ${item.styleCodes.filter(c => !isValidStyleCode(c)).join(', ')}`} arrow>
                                  <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 18 }} />
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Validation error summary */}
                {importData.length > 0 && importData.some((item) => !isRowValid(item)) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#FFF3F0', border: '1px solid #FFCDD2', borderRadius: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ErrorOutlineIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                        {importData.filter((item) => !isRowValid(item)).length} row(s) have validation errors and cannot be selected
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 3.5 }}>
                      {importData.map((item, index) =>
                        getRowErrors(item, index).map((error, errIdx) => (
                          <Typography key={`${index}-${errIdx}`} variant="body2" sx={{ fontSize: '0.8rem', color: '#b71c1c', mb: 0.3 }}>
                            • {error}
                          </Typography>
                        ))
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5, pl: 3.5, fontWeight: 600, color: '#d32f2f', fontSize: '0.85rem' }}>
                      Please correct the errors in your Excel file and re-upload the document.
                    </Typography>
                  </Box>
                )}

                {/* Bulk create results */}
                {bulkCreateResults.length > 0 && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F5F5F5', borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Creation Results ({bulkCreateResults.filter(r => r.success).length}/{bulkCreateResults.length} successful)
                    </Typography>
                    {bulkCreateResults.map((r, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {r.success ? (
                          <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                        ) : (
                          <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 16 }} />
                        )}
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: r.success ? '#333' : '#d32f2f' }}>
                          <strong>{r.ruleName}</strong> — {r.message}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Create Rules button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, gap: 2 }}>
                  {selectedImportRows.size > 0 && (
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {selectedImportRows.size} row{selectedImportRows.size !== 1 ? 's' : ''} selected
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleBulkCreateRules}
                    disabled={selectedImportRows.size === 0 || bulkCreating}
                    startIcon={bulkCreating ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      bgcolor: '#4CAF50',
                      px: 4,
                      py: 1,
                      borderRadius: '8px',
                      '&:hover': { bgcolor: '#388E3C' },
                      '&.Mui-disabled': { bgcolor: '#E0E0E0' },
                    }}
                  >
                    {bulkCreating ? 'Creating...' : `Create Rule${selectedImportRows.size !== 1 ? 's' : ''}`}
                  </Button>
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  // Default empty view
  return (
    <Box
      sx={{
        flex: 1,
        background: '#fff',
        minHeight: '100%'
      }}
    >
      {/* Empty state - no content by default */}
    </Box>
  );
};

export default MainContent;
